import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { rolesService } from '../services/roles/roles.service'
import type {
  RoleDetail,
  RoleListData,
  RoleListParams,
  RoleModuleNode,
} from '../services/roles/roles.types'
import { roleKeys } from './roleKeys'

type UseRolesOptions = Omit<
  UseQueryOptions<
    RoleListData,
    Error,
    RoleListData,
    ReturnType<typeof roleKeys.list>
  >,
  'queryKey' | 'queryFn'
>

/** `GET /roles` — paginated, param-scoped role list. */
export const useRolesQuery = (
  params: RoleListParams,
  options?: UseRolesOptions
) =>
  useQuery({
    queryKey: roleKeys.list(params),
    queryFn: () => rolesService.fetchRoles(params),
    placeholderData: (previous) => previous,
    ...options,
  })

type UseRoleDetailOptions = Omit<
  UseQueryOptions<
    RoleDetail,
    Error,
    RoleDetail,
    ReturnType<typeof roleKeys.detail>
  >,
  'queryKey' | 'queryFn'
>

/** `GET /roles/{roleId}` — single role detail; disabled until `roleId > 0`. */
export const useRoleDetailQuery = (
  roleId: number,
  options?: UseRoleDetailOptions
) =>
  useQuery({
    queryKey: roleKeys.detail(roleId),
    queryFn: () => rolesService.fetchRoleDetail(roleId),
    enabled: roleId > 0,
    ...options,
  })

type UseRoleModulesOptions = Omit<
  UseQueryOptions<
    RoleModuleNode[],
    Error,
    RoleModuleNode[],
    ReturnType<typeof roleKeys.modules>
  >,
  'queryKey' | 'queryFn'
>

/** `GET /roles/modules` — feature dependency graph for the permission tree. */
export const useRoleModulesQuery = (options?: UseRoleModulesOptions) =>
  useQuery({
    queryKey: roleKeys.modules(),
    queryFn: rolesService.fetchRoleModules,
    staleTime: 5 * 60 * 1000,
    ...options,
  })
